import type { BaseSelectionResponse } from "@frend-digital/centra-types/selection";
import type { BeforeEvent } from "@frend-digital/events/gtm";
import { addLeadingSlash } from "@frend-digital/utils";
import type { RefObject } from "react";

export type KlaviyoItem = {
	Name: string;
	ProductId: string;
	Price: number;
	ImageURL: string;
	URL: string;
	SKU: string;
	Categories?: string[];
};

export type KlaviyoCartItem = {
	ProductId: string;
	ProductName: string;
	ProductURL: string;
	ImageURL: string;
	SKU: string;
	Quantity: number;
	ItemPrice: number;
	RowTotal: number;
	ProductCategories: string[];
};

export type KlaviyoAddToCart = {
	$value: number;
	AddedItemProductId: string;
	AddedItemProductName: string;
	AddedItemURL: string;
	AddedItemImageURL: string;
	AddedItemSKU: string;
	AddedItemQuantity: number;
	AddedItemPrice: number;
	AddedItemRowTotal: number;
	AddedItemCategories: string[];
	ItemNames: string[];
	CheckoutURL: string;
	Items: KlaviyoCartItem[];
};

const findCartItem = (itemId: string, selection: BaseSelectionResponse) => {
	const item = selection.selection?.items?.find((item) => {
		const realSku = item.sku?.replace(item.product?.sku || "@@unmatch", "");

		return realSku === itemId;
	});

	if (!item) return undefined;
	return item;
};

type SelectionItem = NonNullable<NonNullable<BaseSelectionResponse["selection"]>["items"]>[number];

const centraCartItemToKlaviyo = (item: SelectionItem, meta: KlaviyoMeta) => {
	try {
		return {
			ImageURL: item.product?.media?.standard?.[0]!,
			ItemPrice: item.priceEachAsNumber!,
			ProductId: item.product?.productSku!,
			ProductName: item.product?.name!,
			SKU: item.sku?.replace(item.product?.sku || "@@unmatch", "")!,
			Quantity: item.quantity!,
			RowTotal: item.totalPriceAsNumber!,
			ProductCategories: item.product?.categories
				?.map((category) => {
					try {
						return category.name?.[category.name?.length - 1];
					} catch (error) {
						return null;
					}
				})
				.filter(Boolean)!,
			ProductURL: `https://vikingfootwear.com/${meta.locale}${addLeadingSlash(item.product?.categoryUri || "")}${addLeadingSlash(item.product?.uri!)}`,
		} satisfies KlaviyoCartItem;
	} catch (error) {
		return undefined;
	}
};

interface KlaviyoMeta {
	locale: string;
}

/**
 * Maps shoe size to size group for kids' footwear
 */
const getSizeGroup = (size: number): string | undefined => {
	if (size >= 19 && size <= 23) return "19-23";
	if (size >= 24 && size <= 27) return "24-27";
	if (size >= 28 && size <= 31) return "28-31";
	if (size >= 32 && size <= 35) return "32-35";
	if (size >= 36 && size <= 39) return "36-39";
	return undefined;
};

/**
 * Checks if the size is kids' footwear (19-39)
 */
const isKidsFootwear = (size: number): boolean => {
	return size >= 19 && size <= 39;
};

/**
 * Parses shoe size from string to number
 */
const parseShoeSize = (sizeString: string | undefined): number | undefined => {
	if (!sizeString) return undefined;
	const parsed = Number.parseInt(sizeString, 10);
	return Number.isNaN(parsed) ? undefined : parsed;
};

export const klaviyoAddToCart = (
	e: Parameters<BeforeEvent>[0],
	selectionRef: RefObject<BaseSelectionResponse | undefined>,
	meta: KlaviyoMeta,
): {
	klaviyo?: KlaviyoAddToCart;
} => {
	try {
		if (!selectionRef.current) return {};

		if (e.event !== "add_to_cart") {
			return {};
		}

		const itemId = e.ecommerce.items[e.ecommerce.items.length - 1].item_id;

		const addedCentraItem = findCartItem(itemId, selectionRef.current);

		if (!addedCentraItem) return {};

		const addedItem = centraCartItemToKlaviyo(addedCentraItem, meta);

		if (!addedItem) return {};

		return {
			klaviyo: {
				$value: selectionRef.current.selection?.totals.grandTotalPriceAsNumber!,
				AddedItemCategories: addedItem.ProductCategories,
				AddedItemImageURL: addedItem.ImageURL,
				AddedItemProductName: addedItem.ProductName,
				AddedItemPrice: addedItem.ItemPrice,
				AddedItemProductId: addedItem.ProductId,
				AddedItemSKU: addedItem.SKU,
				AddedItemQuantity: addedItem.Quantity,
				AddedItemRowTotal: addedItem.RowTotal,
				AddedItemURL: addedItem.ProductURL,
				CheckoutURL: `https://vikingfootwear.com/${meta.locale}/checkout`,
				Items:
					selectionRef.current.selection?.items
						?.map((item) => centraCartItemToKlaviyo(item, meta))
						.filter(Boolean) || [],
				ItemNames:
					selectionRef.current.selection?.items
						?.map((item) => item.product?.name)
						.filter(Boolean) || [],
			},
		};
	} catch (error) {
		return {};
	}
};

/**
 * Adds Klaviyo-specific data for purchase events with kids' footwear size information
 * Returns event-level properties: shoe_size, size_group, and product_category
 */
export const klaviyoAddPurchaseData = (
	e: Parameters<BeforeEvent>[0],
	selectionRef: RefObject<BaseSelectionResponse | undefined>,
): Record<string, unknown> => {
	try {
		if (!selectionRef.current) return {};

		if (e.event !== "purchase") {
			return {};
		}

		// Get Centra selection items
		const items = selectionRef.current.selection?.items;
		if (!items || items.length === 0) return {};

		// Find the first kids' footwear item in the order
		for (const item of items) {
			const shoeSize = parseShoeSize(item.size);

			if (shoeSize && isKidsFootwear(shoeSize)) {
				const sizeGroup = getSizeGroup(shoeSize);

				// Return event-level properties for kids' footwear
				return {
					shoe_size: shoeSize.toString(),
					size_group: sizeGroup,
					product_category: "kids-footwear",
				};
			}
		}

		return {};
	} catch (error) {
		return {};
	}
};
